import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { buttonStyles } from '@/lib/button-styles'

interface CopyAddressButtonProps {
    address: string
}

const copyWithFallback = (text: string): boolean => {
    const textarea = document.createElement('textarea')

    textarea.value = text
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()

    let copied = false

    try {
        copied = document.execCommand('copy')
    }

    catch {
        copied = false
    }

    document.body.removeChild(textarea)

    return copied
}

const CopyAddressButton: FC<CopyAddressButtonProps> = ({ address }): ReactNode => {
    const [copied, setCopied] = useState(false)

    useEffect(() => {
        if (!copied) return

        const timer = window.setTimeout(() => setCopied(false), 2500)

        return () => window.clearTimeout(timer)
    }, [copied])

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(address)
            setCopied(true)
        }

        catch {
            setCopied(copyWithFallback(address))
        }
    }

    return (
        <>
            <button type='button' onClick={handleCopy} className={buttonStyles({ variant: 'outline' })}>
                {copied ? <Check aria-hidden='true' /> : <Copy aria-hidden='true' />}
                {copied ? "Adresse copiée !" : "Copier l’adresse"}
            </button>

            <span className='sr-only' aria-live='polite'>
                {copied ? "L’adresse a été copiée." : ''}
            </span>
        </>
    )
}

export default CopyAddressButton
